// src/components/sidebarcart/CartItemQuantityInput.js
import React, { useState, useCallback, useEffect, useRef } from 'react'; // Import useRef
import PropTypes from 'prop-types';
import { Box, IconButton, TextField } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Helper function for reliable rounding (Keep this identical to the one in CartProvider.js)
const roundToDecimals = (num, decimals) => {
    if (typeof num !== 'number' || isNaN(num)) {
        return 0;
    }
    const factor = Math.pow(10, decimals);
    return Math.round(num * factor) / factor;
};

// --- ItemQuantityInput Component ---
function ItemQuantityInput({ productId, initialQuantity, onUpdateQuantity }) {
    // localInputValue will store the raw string from the TextField for direct typing
    const [localInputValue, setLocalInputValue] = useState(String(initialQuantity));
    const [error, setError] = useState(false);
    const [helperText, setHelperText] = useState('');
    const inputRef = useRef(null); // Ref to check if input is focused

    const MIN_QUANTITY = 0.01;
    const MAX_QUANTITY = 9999;
    const BUTTON_STEP = 1; // Step for up/down buttons (integer)
    const DISPLAY_DECIMALS = 2; // Number of decimal places for display and rounding

    // Synchronize local state with initialQuantity from parent (CartProvider)
    useEffect(() => {
        const roundedInitial = roundToDecimals(initialQuantity, DISPLAY_DECIMALS);
        // Only update local input if it's not currently being edited AND the actual numerical value differs
        // or if the string representation is different (e.g., "0.5" vs "0.50")
        const currentNumericalValue = parseFloat(localInputValue);
        const currentRoundedValue = roundToDecimals(currentNumericalValue, DISPLAY_DECIMALS);

        // Check if the input is currently focused. If it is, don't force an update
        // that could disrupt typing.
        if (inputRef.current && document.activeElement === inputRef.current) {
            // If focused, let the user continue typing. Don't sync from parent.
            // However, if the current value is completely invalid, we might want to override.
            // For now, we'll trust the blur handler to fix it.
            return;
        }

        // If not focused, or if the numerical value is definitively different from the rounded initialQuantity,
        // update the local state.
        if (currentRoundedValue !== roundedInitial) {
            setLocalInputValue(String(roundedInitial)); // Update the string for display
        }
        setError(false);
        setHelperText('');
    }, [initialQuantity, localInputValue, productId]); // Re-evaluate when initialQuantity, localInputValue, or productId change

    // This function will perform the validation and send the *final* quantity to the parent
    const validateAndSendToParent = useCallback((newValueNum) => {
        let validatedQuantity = roundToDecimals(newValueNum, DISPLAY_DECIMALS);

        if (isNaN(validatedQuantity)) {
            validatedQuantity = MIN_QUANTITY; // Default to min if not a valid number after parsing
        }

        // Apply boundary conditions directly
        if (validatedQuantity < MIN_QUANTITY) {
            validatedQuantity = MIN_QUANTITY;
            setError(true);
            setHelperText(`Mínimo ${MIN_QUANTITY}`);
        } else if (validatedQuantity > MAX_QUANTITY) {
            validatedQuantity = MAX_QUANTITY;
            setError(true);
            setHelperText(`Máximo ${MAX_QUANTITY}`);
        } else {
            setError(false);
            setHelperText('');
        }

        // Only update parent if the validated quantity truly changed from the *current actual* quantity in cart
        // (which is `initialQuantity` from props, as that's the source of truth from CartProvider)
        if (validatedQuantity !== initialQuantity) {
            onUpdateQuantity(productId, validatedQuantity); // Pass the final, rounded total quantity
        }
        // Always update local input value with the final validated, rounded number for display
        setLocalInputValue(String(validatedQuantity)); 

    }, [initialQuantity, onUpdateQuantity, productId]);

    // Handler for direct input changes (as user types)
    const handleLocalChange = useCallback((e) => {
        const value = e.target.value;
        // Allow typing numbers, a single decimal point, and trailing zeros.
        // This regex ensures we only accept valid numerical input patterns, including incomplete ones.
        if (/^\d*\.?\d*$/.test(value)) { // Allows "1", "1.", "1.5", ".5", "0.50"
            setLocalInputValue(value); // Store the raw string typed by the user
            setError(false);
            setHelperText('');
        }
    }, []);

    // Handler for input blur (when user finishes typing or clicks away)
    const handleLocalBlur = useCallback(() => {
        let valueToParse = localInputValue.trim();

        // Handle cases like "5." becoming "5" or an empty string becoming MIN_QUANTITY
        if (valueToParse.endsWith('.')) {
            valueToParse = valueToParse.slice(0, -1);
        }
        
        const num = parseFloat(valueToParse);
        // If the parsed number is NaN (e.g., input was "", ".") or outside a reasonable range for a number input
        if (isNaN(num)) {
            validateAndSendToParent(MIN_QUANTITY); // Default to MIN_QUANTITY if not a valid number
        } else {
            validateAndSendToParent(num);
        }
    }, [localInputValue, validateAndSendToParent]);

    // Handler for Enter key press
    const handleLocalKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.target.blur(); // Trigger onBlur to apply validation and update
        }
    }, []);

    // Handler for Increment button
    const handleIncrement = useCallback(() => {
        // Parse the current local input value as a number for calculation.
        // Use initialQuantity as a fallback if localInputValue is not a valid number during operation
        const currentNum = parseFloat(localInputValue);
        const baseQuantity = isNaN(currentNum) ? initialQuantity : currentNum; // Use actual number or fallback
        const newQuantity = baseQuantity + BUTTON_STEP;
        validateAndSendToParent(newQuantity);
    }, [localInputValue, initialQuantity, validateAndSendToParent]);

    // Handler for Decrement button
    const handleDecrement = useCallback(() => {
        const currentNum = parseFloat(localInputValue);
        const baseQuantity = isNaN(currentNum) ? initialQuantity : currentNum; // Use actual number or fallback
        const newQuantity = baseQuantity - BUTTON_STEP;
        validateAndSendToParent(newQuantity);
    }, [localInputValue, initialQuantity, validateAndSendToParent]);

    // Dynamic font size calculation based on the length of the *potential* displayed value.
    // It's safer to base this on the actual parsed and rounded value for consistent sizing.
    const effectiveDisplayedValue = String(roundToDecimals(parseFloat(localInputValue || initialQuantity), DISPLAY_DECIMALS));
    const currentDisplayedLength = effectiveDisplayedValue.length;
    const fontSize = currentDisplayedLength > 5 ? '0.65em' : currentDisplayedLength > 3 ? '0.8em' : '1em';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
                size="small"
                onClick={handleDecrement}
                disabled={roundToDecimals(parseFloat(localInputValue || initialQuantity), DISPLAY_DECIMALS) <= MIN_QUANTITY}
            >
                <ArrowDropDownIcon fontSize="large" sx={{ color: '#595959' }} />
            </IconButton>
            <TextField
                id={`quantity-input-${productId}`}
                inputRef={inputRef} // Attach ref here
                variant="standard"
                size="small"
                // IMPORTANT: When input is focused, display the raw localInputValue.
                // When unfocused, or initially, display the rounded version of initialQuantity.
                // This makes typing feel natural.
                value={
                    (inputRef.current && document.activeElement === inputRef.current)
                        ? localInputValue // Show raw input string when focused
                        : String(roundToDecimals(initialQuantity, DISPLAY_DECIMALS)) // Show rounded initialQuantity when unfocused
                }
                onChange={handleLocalChange}
                onBlur={handleLocalBlur}
                onKeyDown={handleLocalKeyDown}
                type="text" // Crucial: Keep "text" to allow custom validation for decimals
                error={error}
                helperText={helperText}
                inputProps={{
                    min: MIN_QUANTITY, // Still useful for assistive technologies
                    max: MAX_QUANTITY,
                    step: BUTTON_STEP, // This 'step' is mostly for accessibility, not enforced by type="text"
                }}
                sx={{
                    width: '55px',
                    '& .MuiInputBase-root': { // Targets the root of the input for 'standard' variant
                        height: '32px', // Match button height
                    },
                    '& .MuiInputBase-input': {
                        padding: '4px 2px', // Adjust inner input padding for better visibility
                        textAlign: 'center', // Center text within the input
                        color: '#262626', // Dark text for light theme
                        fontWeight: 500, // Make text slightly bolder for visibility
                        // Remove default spin buttons (though type="text" handles this usually)
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                        fontSize: fontSize, // Apply dynamic font size
                    },
                    // Styles for the standard variant underline (borders)
                    '& .MuiInput-underline:before': { // Default underline
                        borderBottomColor: '#d9d9d9',
                    },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { // Hover underline
                        borderBottomColor: '#40a9ff',
                    },
                    '& .MuiInput-underline:after': { // Focused underline
                        borderBottomColor: '#1890ff',
                    },
                    // Style for helper text (error message)
                    '& .MuiFormHelperText-root.Mui-error': {
                        color: '#ffb3b3', // Lighter red for visibility
                        fontSize: '0.6rem', // Smaller font size
                        position: 'absolute', // Position it above or below as needed
                        top: '100%', // Place it below the input field
                        left: '50%',
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap', // Prevent wrapping
                        mt: 0.5, // Margin top
                        textAlign: 'center',
                        width: '150%', // Make it wider to fit text
                        overflow: 'visible'
                    },
                }}
            />
            <IconButton
                size="small"
                onClick={handleIncrement}
                disabled={roundToDecimals(parseFloat(localInputValue || initialQuantity), DISPLAY_DECIMALS) >= MAX_QUANTITY}
            >
                <ArrowDropUpIcon fontSize="large" sx={{ color: '#595959' }} />
            </IconButton>
        </Box>
    );
}

ItemQuantityInput.propTypes = {
    productId: PropTypes.string.isRequired,
    initialQuantity: PropTypes.number.isRequired,
    onUpdateQuantity: PropTypes.func.isRequired,
};

// Memoize to prevent re-renders when parent re-renders but props haven't changed
export default React.memo(ItemQuantityInput, (prevProps, nextProps) => {
    return (
        prevProps.productId === nextProps.productId &&
        prevProps.initialQuantity === nextProps.initialQuantity &&
        prevProps.onUpdateQuantity === nextProps.onUpdateQuantity
    );
});